/**
 * DateInput.jsx
 *
 * Drop-in replacement for a native <input type="date"> with a themed calendar
 * popover (the native picker's day grid is browser chrome and can't be styled).
 * Follows the SearchableSelect pattern: the popover renders via React Portal
 * into document.body with fixed positioning so it escapes overflow clipping.
 *
 * Props:
 *   id           — id for the trigger button (so <label htmlFor=…> works)
 *   name         — passed through in the onChange event + a hidden input
 *   value        — ISO date string 'YYYY-MM-DD' or ''
 *   onChange     — fired with { target: { name, value } } so it slots into
 *                  existing handlers reading e.target.name / e.target.value
 *   min / max    — ISO date strings; days outside the range are disabled
 *   placeholder  — text when empty (default 'dd/mm/yyyy')
 *   className    — trigger class (default 'input' to match form fields)
 *   style        — inline style for the trigger (error borders, compact filters)
 *   disabled
 *   clearable    — show a Clear action in the footer when a value is set
 *   popoverZIndex — stacking order (must exceed modal overlays)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const POP_WIDTH  = 276;
const POP_HEIGHT = 352;   // estimate for flip-up decision
const POP_GAP    = 4;

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const DOW = ['Mo','Tu','We','Th','Fr','Sa','Su'];

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const pad2 = (n) => String(n).padStart(2, '0');
const toIso = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

function parseIso(s) {
  if (!s || !ISO_RE.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m: m - 1, d };
}

// Local-time "today" (YYYY-MM-DD). Exported for call sites passing min/max —
// new Date().toISOString() is UTC and lags local time by an hour overnight
// (IST), which made "today" fall outside its own max between midnight and 1am.
export function todayIso() {
  const t = new Date();
  return toIso(t.getFullYear(), t.getMonth(), t.getDate());
}

// ISO strings compare correctly as plain strings — no Date/TZ needed.
const inRange = (iso, min, max) =>
  (!min || iso >= min) && (!max || iso <= max);

const formatDisplay = (iso) => {
  const p = parseIso(iso);
  return p ? `${pad2(p.d)}/${pad2(p.m + 1)}/${p.y}` : '';
};

// 42 cells (6 weeks), Monday-first, spanning the neighbours of (y, m).
function buildGrid(y, m) {
  const first = new Date(y, m, 1);
  const lead = (first.getDay() + 6) % 7;   // Mon=0 … Sun=6
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const dt = new Date(y, m, 1 - lead + i);
    cells.push({
      iso: toIso(dt.getFullYear(), dt.getMonth(), dt.getDate()),
      day: dt.getDate(),
      out: dt.getMonth() !== m,
    });
  }
  return cells;
}

export default function DateInput({
  id,
  name,
  value = '',
  onChange,
  min,
  max,
  placeholder = 'dd/mm/yyyy',
  className = 'input',
  style,
  disabled = false,
  clearable = true,
  popoverZIndex = 10001,
}) {
  const [open, setOpen]         = useState(false);
  const [popStyle, setPopStyle] = useState(null);
  const [view, setView]         = useState(() => {
    const p = parseIso(value) || parseIso(todayIso());
    return { y: p.y, m: p.m };
  });
  const [cursor, setCursor]     = useState(value || todayIso());

  const triggerRef = useRef(null);
  const popRef     = useRef(null);

  const today = todayIso();

  // Opening → view the selected month (or today's), park the keyboard cursor there.
  useEffect(() => {
    if (!open) return;
    const start = parseIso(value) ? value : today;
    const p = parseIso(start);
    setView({ y: p.y, m: p.m });
    setCursor(start);
  }, [open]);                                            // eslint-disable-line react-hooks/exhaustive-deps

  // ── Popover positioning (same approach as SearchableSelect) ───────────────
  const updatePosition = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const rect = trig.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom;
    const openUp = spaceBelow < POP_HEIGHT + POP_GAP + 12 && rect.top > spaceBelow;
    setPopStyle({
      top:    openUp ? undefined : rect.bottom + POP_GAP,
      bottom: openUp ? (vh - rect.top + POP_GAP) : undefined,
      left:   Math.max(8, Math.min(rect.left, vw - POP_WIDTH - 8)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Outside-click: the popover is portalled, so check both roots.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (triggerRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target))     return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Keyboard navigation happens on the popover itself.
  useEffect(() => {
    if (open && popStyle) popRef.current?.focus();
  }, [open, popStyle]);

  function emit(next) {
    onChange?.({ target: { name, value: next } });
  }

  function commit(iso) {
    if (!inRange(iso, min, max)) return;
    emit(iso);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function moveCursor(days, months = 0) {
    const p = parseIso(cursor) || parseIso(today);
    const dt = new Date(p.y, p.m + months, p.d + days);
    const iso = toIso(dt.getFullYear(), dt.getMonth(), dt.getDate());
    setCursor(iso);
    setView({ y: dt.getFullYear(), m: dt.getMonth() });
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowLeft')       { e.preventDefault(); moveCursor(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); moveCursor(1); }
    else if (e.key === 'ArrowUp')    { e.preventDefault(); moveCursor(-7); }
    else if (e.key === 'ArrowDown')  { e.preventDefault(); moveCursor(7); }
    else if (e.key === 'PageUp')     { e.preventDefault(); moveCursor(0, -1); }
    else if (e.key === 'PageDown')   { e.preventDefault(); moveCursor(0, 1); }
    else if (e.key === 'Enter')      { e.preventDefault(); commit(cursor); }
    else if (e.key === 'Escape') {
      e.preventDefault(); e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  function shiftView(months, years = 0) {
    setView(v => {
      const dt = new Date(v.y + years, v.m + months, 1);
      return { y: dt.getFullYear(), m: dt.getMonth() };
    });
  }

  const display = formatDisplay(value);
  const grid = open ? buildGrid(view.y, view.m) : [];

  const popover = open && !disabled && popStyle && createPortal(
    <div
      ref={popRef}
      className="dp-pop"
      role="dialog"
      aria-label="Choose date"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed', zIndex: popoverZIndex, width: POP_WIDTH,
        top: popStyle.top, bottom: popStyle.bottom, left: popStyle.left,
      }}
    >
      <div className="dp-head">
        <button type="button" className="dp-nav" aria-label="Previous year"  onClick={() => shiftView(0, -1)}>«</button>
        <button type="button" className="dp-nav" aria-label="Previous month" onClick={() => shiftView(-1)}>‹</button>
        <div className="dp-title">{MONTHS[view.m]} {view.y}</div>
        <button type="button" className="dp-nav" aria-label="Next month"     onClick={() => shiftView(1)}>›</button>
        <button type="button" className="dp-nav" aria-label="Next year"      onClick={() => shiftView(0, 1)}>»</button>
      </div>
      <div className="dp-grid" role="grid">
        {DOW.map(d => <div key={d} className="dp-dow">{d}</div>)}
        {grid.map(c => {
          const dis = !inRange(c.iso, min, max);
          return (
            <button
              key={c.iso}
              type="button"
              disabled={dis}
              aria-label={c.iso}
              aria-current={c.iso === today ? 'date' : undefined}
              className={
                'dp-day' +
                (c.out ? ' out' : '') +
                (c.iso === value ? ' selected' : '') +
                (c.iso === today ? ' today' : '') +
                (c.iso === cursor ? ' cursor' : '')
              }
              onMouseEnter={() => setCursor(c.iso)}
              onMouseDown={(e) => { e.preventDefault(); commit(c.iso); }}
            >
              {c.day}
            </button>
          );
        })}
      </div>
      <div className="dp-foot">
        <button
          type="button" className="dp-action"
          disabled={!inRange(today, min, max)}
          onMouseDown={(e) => { e.preventDefault(); commit(today); }}
        >Today</button>
        {clearable && value && (
          <button
            type="button" className="dp-action"
            onMouseDown={(e) => { e.preventDefault(); emit(''); setOpen(false); triggerRef.current?.focus(); }}
          >Clear</button>
        )}
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={`${className} dp-trigger`.trim()}
        style={style}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        <span className={display ? 'dp-value' : 'dp-placeholder'}>
          {display || placeholder}
        </span>
        <svg className="dp-icon" width="15" height="15" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      {/* Hidden input so the value participates in form submissions if needed */}
      <input type="hidden" name={name} value={value || ''} readOnly />
      {popover}
    </>
  );
}
